/**
 * Verantwortlichkeit (PSIRT) — who answers for each product under the CRA.
 *
 * Two layers, because they change at different speeds and are owned by
 * different people:
 *
 *   PSIRT-Teams   organisational, shared by many products: Meldestelle,
 *                 CVD-Policy, the Art. 14 reporting channel to CSIRT/ENISA.
 *   Produkte      one responsibility per product: team, Product Security
 *                 Owner, CRA category, support period, update channel.
 *
 * The verdict per product (and per team) comes from
 * app/services/psirt_service.py and is the same one the CRA-Check shows as its
 * "Verantwortlichkeit (PSIRT)" rule. Writes go through the generic Stammdaten
 * CRUD (`cmdb.write`); an auditor sees everything read-only.
 */

import type { MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { useLingui as useLinguiContext } from '@lingui/react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../components/Banner';
import Chip from '../components/Chip';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchPsirtOverview, provisionAssetRadarSchema } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DASH, fmtDate } from '../lib/format';
import { intlLocale } from '../lib/locale';
import type {
  KpiCounter,
  PsirtCheck,
  PsirtOverviewResponse,
  PsirtProductRow,
  PsirtState,
  PsirtTeam,
} from '../lib/types';

import AssignTeamModal from './psirt/AssignTeamModal';
import ProductFormModal from './psirt/ProductFormModal';
import TeamFormModal from './psirt/TeamFormModal';
import {
  KATEGORIE_SHORT,
  KATEGORIE_TONE,
  PSIRT_STATE_LABELS,
  PSIRT_STATE_RANK,
  PSIRT_STATE_TONE,
  fmtDayIso,
  fmtSpan,
  stateName,
} from './psirt/psirtModel';

import './psirt/psirt.css';

type Filter = 'alle' | PsirtState | 'ohne-team' | 'support' | 'pruefung';

const FILTERS: readonly { key: Filter; label: MessageDescriptor }[] = [
  { key: 'alle', label: msg`Alle Produkte` },
  { key: 'fehler', label: msg`Lücken` },
  { key: 'warnung', label: msg`Hinweise` },
  { key: 'ohne-team', label: msg`Ohne Team` },
  { key: 'ok', label: msg`Vollständig` },
  { key: 'support', label: msg`Support endet ≤ 12 Mon.` },
  { key: 'pruefung', label: msg`Prüfung fällig` },
];

/** Start of the Art. 14 reporting obligations, as a day in the UI language. */
function reportingObligationsStart(): string {
  return new Date(Date.UTC(2026, 8, 11))
    .toLocaleDateString(intlLocale(), { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' })
    .replaceAll(' ', '\u00a0');
}

/** Support ends within the warning window or has already ended (same rule as
 *  the summary: support_bald + support_abgelaufen). */
function supportEndingSoon(row: PsirtProductRow, warnMonths: number): boolean {
  if (!row.support_ende || row.support_restmonate === null) return false;
  const expired = row.support_ende < new Date().toISOString().slice(0, 10);
  return expired || row.support_restmonate < warnMonths;
}

/** Review of the responsibility record is due (same rule as the summary). */
function reviewDue(row: PsirtProductRow): boolean {
  return row.verantwortung_id !== null && row.checks.some((check) => check.key === 'review' && check.state !== 'ok');
}

function matchesFilter(row: PsirtProductRow, filter: Filter, warnMonths: number): boolean {
  if (filter === 'alle') return true;
  if (filter === 'ohne-team') return row.team_id === null;
  if (filter === 'support') return supportEndingSoon(row, warnMonths);
  if (filter === 'pruefung') return reviewDue(row);
  return row.state === filter;
}

type Dialog =
  | { kind: 'team'; team: PsirtTeam | null; returnTo?: PsirtProductRow }
  | { kind: 'product'; row: PsirtProductRow }
  | { kind: 'assign' };

function CheckItems({ checks }: { checks: readonly PsirtCheck[] }): ReactNode {
  useLinguiContext(); // PSIRT_STATE_LABELS is read in the UI language.
  return (
    <ul className="psirt-checks">
      {checks.map((check) => (
        <li key={check.key} data-state={check.state}>
          <span className="psirt-dot" aria-hidden="true" />
          <div>
            <div className="psirt-check-label">
              {check.label}
              <span className="visually-hidden">, {PSIRT_STATE_LABELS[check.state]}</span>
            </div>
            <div className="psirt-check-detail">{check.detail}</div>
            {check.hint && check.state !== 'ok' ? <div className="psirt-check-hint">{check.hint}</div> : null}
          </div>
          <span className="psirt-check-ref">{check.reference}</span>
        </li>
      ))}
    </ul>
  );
}

function Progress({ done, total, state }: { done: number; total: number; state: PsirtState }): ReactNode {
  const { t } = useLingui();
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="psirt-progress" title={t`${done} von ${total} Anforderungen erfüllt`}>
      <span className="psirt-meter" data-state={state}>
        <span style={{ width: `${percent}%` }} />
      </span>
      <span className="psirt-progress-text">
        {done}/{total}
      </span>
    </div>
  );
}

function TeamCard({
  team,
  canWrite,
  onEdit,
}: {
  team: PsirtTeam;
  canWrite: boolean;
  onEdit: () => void;
}): ReactNode {
  const { t } = useLingui();
  const v = team.values;
  const open = team.checks.filter((check) => check.state !== 'ok');
  const openList = open.map((check) => check.label).join(', ');
  const total = team.gesamt;
  const deputy = v.VertretungName;
  return (
    <article className="psirt-team" data-state={team.state}>
      <header>
        <div className="psirt-team-title">
          <div>
            <h3>{team.label}</h3>
            <span className="muted">
              {team.produkte === 0
                ? t`noch keinem Produkt zugeordnet`
                : t`${plural(team.produkte, { one: '# Produkt', other: '# Produkte' })}`}
            </span>
          </div>
        </div>
        <Chip tone={PSIRT_STATE_TONE[team.state]}>{PSIRT_STATE_LABELS[team.state]}</Chip>
      </header>

      <dl className="psirt-team-facts">
        <div>
          <dt><Trans>Meldestelle</Trans></dt>
          <dd>{v.MeldeEmail ? <a href={`mailto:${v.MeldeEmail}`}>{v.MeldeEmail}</a> : <em><Trans>fehlt</Trans></em>}</dd>
        </div>
        <div>
          <dt><Trans>Erreichbarkeit</Trans></dt>
          <dd>
            {v.Erreichbarkeit || <em><Trans>nicht angegeben</Trans></em>}
            {v.Telefon ? <span className="muted"> · {v.Telefon}</span> : null}
          </dd>
        </div>
        <div>
          <dt><Trans>Koordinierendes CSIRT</Trans></dt>
          <dd>
            {v.CsirtKoordinator || <em><Trans>fehlt</Trans></em>}
            {v.Mitgliedstaat ? <span className="muted"> · {stateName(v.Mitgliedstaat)}</span> : null}
          </dd>
        </div>
        <div>
          <dt><Trans>Meldet an ENISA</Trans></dt>
          <dd>{v.MelderName || v.MelderEmail || <em><Trans>fehlt</Trans></em>}</dd>
        </div>
        <div>
          <dt><Trans>Leitung</Trans></dt>
          <dd>
            {v.LeitungName || <em><Trans>nicht benannt</Trans></em>}
            {deputy ? <span className="muted"> · <Trans>Vertretung {deputy}</Trans></span> : null}
          </dd>
        </div>
        <div>
          <dt><Trans>Offenlegung</Trans></dt>
          <dd className="psirt-links">
            {v.CvdPolicyUrl ? (
              <a href={v.CvdPolicyUrl} target="_blank" rel="noreferrer noopener">
                <Trans>CVD-Policy</Trans> <Icon name="external" size={12} />
              </a>
            ) : (
              <em><Trans>CVD-Policy fehlt</Trans></em>
            )}
            {v.SecurityTxtUrl ? (
              <a href={v.SecurityTxtUrl} target="_blank" rel="noreferrer noopener">
                security.txt <Icon name="external" size={12} />
              </a>
            ) : null}
          </dd>
        </div>
      </dl>

      <footer>
        {open.length > 0 ? (
          <span className="psirt-team-open"><Trans>Offen: {openList}</Trans></span>
        ) : (
          <span className="psirt-team-open ok"><Trans>Alle {total} Team-Anforderungen erfüllt</Trans></span>
        )}
        <button type="button" className="small" onClick={onEdit}>
          {canWrite ? t`Bearbeiten` : t`Details`}
        </button>
      </footer>
    </article>
  );
}

export function VerantwortlichkeitPage(): ReactNode {
  const { t, i18n } = useLingui();
  const { has } = useAuth();
  const toast = useToast();
  const canWrite = has('cmdb.write');
  const canProvision = has('schema.admin');

  const [data, setData] = useState<PsirtOverviewResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [filter, setFilter] = useState<Filter>('alle');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const load = useCallback(async (mode: 'initial' | 'refresh'): Promise<PsirtOverviewResponse | null> => {
    if (mode === 'initial') setLoading(true);
    else setRefreshing(true);
    try {
      const response = await fetchPsirtOverview();
      setData(response);
      setLoadError(null);
      return response;
    } catch (error) {
      setLoadError(errorMessage(error));
      return null;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load('initial');
  }, [load]);

  const onSaved = useCallback((): void => {
    setDialog(null);
    void load('refresh');
  }, [load]);

  /** A team created from inside the product dialog returns to that product. */
  const onTeamSaved = useCallback(
    async (returnTo: PsirtProductRow | undefined): Promise<void> => {
      setDialog(null);
      const response = await load('refresh');
      if (!returnTo || !response) return;
      const row = response.rows.find((entry) => entry.produkt_id === returnTo.produkt_id);
      if (row) setDialog({ kind: 'product', row });
    },
    [load],
  );

  const provision = useCallback(async (): Promise<void> => {
    setProvisioning(true);
    try {
      const response = await provisionAssetRadarSchema();
      if (response.status === 'partial') {
        const details = response.result.errors.join('; ');
        toast.error(t`Datenmodell teilweise ergänzt: ${details}`);
      } else {
        toast.success(t`Datenmodell für Verantwortlichkeiten ergänzt.`);
      }
      await load('refresh');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setProvisioning(false);
    }
  }, [load, t, toast]);

  const rows = useMemo((): PsirtProductRow[] => {
    const all = data?.rows ?? [];
    const term = search.trim().toLowerCase();
    return all.filter((row) => {
      if (!matchesFilter(row, filter, data?.thresholds.support_warn_months ?? 12)) return false;
      if (term === '') return true;
      return [row.produkt, row.modellreihe, row.team, row.values?.OwnerName, row.values?.OwnerEmail]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(term));
    });
  }, [data, filter, search]);

  const filterCounts = useMemo((): Record<Filter, number> => {
    const all = data?.rows ?? [];
    return {
      alle: all.length,
      fehler: all.filter((row) => row.state === 'fehler').length,
      warnung: all.filter((row) => row.state === 'warnung').length,
      ok: all.filter((row) => row.state === 'ok').length,
      'ohne-team': all.filter((row) => row.team_id === null).length,
      support: all.filter((row) => supportEndingSoon(row, data?.thresholds.support_warn_months ?? 12)).length,
      pruefung: all.filter(reviewDue).length,
    };
  }, [data]);

  // Each tile filters the table; the filter key (not the translated label) links
  // tile and status select, a second click on the tile goes back to „Alle“.
  const counters = useMemo((): { target: Filter; counter: KpiCounter }[] => {
    const summary = data?.summary;
    if (!summary) return [];
    const supportSoon = summary.support_bald;
    const supportExpired = summary.support_abgelaufen;
    return [
      { target: 'alle', counter: { label: t`Produkte`, value: summary.produkte, tone: 'neutral' } },
      {
        target: 'ok',
        counter: {
          label: t`Vollständig geregelt`,
          value: summary.vollstaendig,
          tone: summary.produkte > 0 && summary.vollstaendig === summary.produkte ? 'good' : 'neutral',
        },
      },
      {
        target: 'fehler',
        counter: {
          label: t`Mit Lücken`,
          value: summary.luecken,
          tone: summary.luecken > 0 ? 'alert' : 'good',
          title: t`Mindestens eine CRA-Pflichtangabe fehlt.`,
        },
      },
      {
        target: 'ohne-team',
        counter: {
          label: t`Ohne PSIRT-Team`,
          value: summary.ohne_zustaendigkeit,
          tone: summary.ohne_zustaendigkeit > 0 ? 'alert' : 'good',
        },
      },
      {
        target: 'support',
        counter: {
          label: t`Support endet ≤ 12 Mon.`,
          value: supportSoon + supportExpired,
          tone: supportSoon + supportExpired > 0 ? 'warn' : 'good',
          title: t`${supportSoon} endet in den nächsten 12 Monaten, ${supportExpired} bereits abgelaufen.`,
        },
      },
      {
        target: 'pruefung',
        counter: {
          label: t`Prüfung fällig`,
          value: summary.pruefung_faellig,
          tone: summary.pruefung_faellig > 0 ? 'warn' : 'good',
          title: t`Noch nie oder vor mehr als 12 Monaten geprüft.`,
        },
      },
    ];
  }, [data, t]);

  const kpiTiles = useMemo(
    (): KpiItem[] =>
      counters.map(({ target, counter }) => {
        const { label, value } = counter;
        return {
          ...counter,
          key: target,
          ariaLabel: t`${label}: ${value} anzeigen`,
          selected: filter === target && search.trim() === '',
          controls: 'psirt-results',
          onClick: () => {
            const active = filter === target && search.trim() === '';
            setFilter(active ? 'alle' : target);
            setSearch('');
          },
        };
      }),
    [counters, filter, search, t],
  );

  const columns = useMemo(
    (): readonly DataTableColumn<PsirtProductRow>[] => [
      {
        key: 'state',
        header: t`Status`,
        width: '118px',
        sortValue: (row) => PSIRT_STATE_RANK[row.state] * 100 + row.erfuellt,
        render: (row) => <Chip tone={PSIRT_STATE_TONE[row.state]}>{PSIRT_STATE_LABELS[row.state]}</Chip>,
      },
      {
        key: 'produkt',
        header: t`Produkt`,
        sortValue: (row) => row.produkt.toLowerCase(),
        render: (row) => {
          const delivered = row.instanzen;
          return (
            <>
              <strong>{row.produkt}</strong>
              <div className="muted">
                {[
                  row.modellreihe,
                  delivered > 0 ? t`${delivered} ausgeliefert` : t`nicht ausgeliefert`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </>
          );
        },
      },
      {
        key: 'team',
        header: t`PSIRT-Team`,
        sortValue: (row) => row.team.toLowerCase() || '~',
        render: (row) => (row.team ? row.team : <span className="psirt-missing"><Trans>nicht zugeordnet</Trans></span>),
      },
      {
        key: 'owner',
        header: t`Security Owner`,
        sortValue: (row) => (row.values?.OwnerName ?? '~').toLowerCase(),
        render: (row) =>
          row.values?.OwnerName ? (
            <>
              <div>{row.values.OwnerName}</div>
              {row.values.OwnerEmail ? (
                <div className="muted psirt-mail" title={row.values.OwnerEmail}>
                  {row.values.OwnerEmail}
                </div>
              ) : null}
            </>
          ) : (
            <span className="psirt-missing"><Trans>nicht benannt</Trans></span>
          ),
      },
      {
        key: 'kategorie',
        header: t`Kategorie`,
        width: '118px',
        sortValue: (row) => row.kategorie ?? '~',
        render: (row) =>
          row.kategorie ? (
            <Chip tone={KATEGORIE_TONE[row.kategorie]}>{KATEGORIE_SHORT[row.kategorie]}</Chip>
          ) : (
            <span className="muted">{DASH}</span>
          ),
      },
      {
        key: 'support',
        header: t`Support bis`,
        width: '132px',
        sortValue: (row) => row.support_ende ?? '9999',
        render: (row) => {
          if (!row.support_ende || row.support_restmonate === null) {
            return <span className="psirt-missing"><Trans>offen</Trans></span>;
          }
          const expired = row.support_ende < new Date().toISOString().slice(0, 10);
          const soon = !expired && row.support_restmonate < (data?.thresholds.support_warn_months ?? 12);
          const span = fmtSpan(row.support_restmonate);
          return (
            <>
              <div>{fmtDayIso(row.support_ende)}</div>
              <div className={expired ? 'psirt-support-cell alert' : soon ? 'psirt-support-cell warn' : 'muted'}>
                {expired ? t`abgelaufen` : t`noch ${span}`}
              </div>
            </>
          );
        },
      },
      {
        key: 'progress',
        header: t`CRA-Angaben`,
        width: '132px',
        sortValue: (row) => row.erfuellt / Math.max(row.gesamt, 1),
        render: (row) => <Progress done={row.erfuellt} total={row.gesamt} state={row.state} />,
      },
      {
        key: 'action',
        header: <span className="visually-hidden"><Trans>Aktion</Trans></span>,
        width: '120px',
        align: 'right',
        render: (row) => (
          <button
            type="button"
            className={`small${canWrite && row.verantwortung_id === null ? ' primary' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              setDialog({ kind: 'product', row });
            }}
          >
            {!canWrite ? t`Details` : row.verantwortung_id === null ? t`Festlegen` : t`Bearbeiten`}
          </button>
        ),
      },
    ],
    [canWrite, data?.thresholds.support_warn_months, t],
  );

  const renderDetail = useCallback(
    (row: PsirtProductRow): ReactNode => {
      const { team } = row;
      return (
        <div className="psirt-detail">
          <div>
            <h3>
              <Trans>Organisation</Trans>{' '}
              {team ? <span className="muted">· <Trans>über {team}</Trans></span> : null}
            </h3>
            <CheckItems checks={row.checks.filter((check) => check.group === 'organisation')} />
          </div>
          <div>
            <h3><Trans>Produkt</Trans></h3>
            <CheckItems checks={row.checks.filter((check) => check.group === 'produkt')} />
          </div>
        </div>
      );
    },
    [],
  );

  if (loading) return <PageSpinner text={t`Lädt Verantwortlichkeiten…`} />;

  const teams = data?.teams ?? [];
  const schemaReady = data?.schema_ready ?? false;
  const obligationsStart = reportingObligationsStart();
  const shown = rows.length;
  const total = data?.rows.length ?? 0;
  const generatedAt = data?.generated_at ? fmtDate(data.generated_at) : null;

  return (
    <>
      <PageHeader
        title={t`Verantwortlichkeit (PSIRT)`}
        sub={
          <>
            <Trans>Meldepflichten nach Art. 14 CRA gelten seit dem {obligationsStart}</Trans>
            {generatedAt ? <> · <Trans>Stand {generatedAt}</Trans></> : null}
            {' · '}
            <Trans>Je Instanz im <Link to="/cra-check">CRA-Check</Link></Trans>
          </>
        }
        actions={
          <>
            {refreshing ? <Spinner label={t`Aktualisiert…`} /> : null}
            <button type="button" onClick={() => void load('refresh')} disabled={refreshing}>
              <Trans>Neu laden</Trans>
            </button>
            {canWrite && schemaReady ? (
              <button type="button" className="primary" onClick={() => setDialog({ kind: 'team', team: null })}>
                <Trans>PSIRT-Team anlegen</Trans>
              </button>
            ) : null}
          </>
        }
      />

      {loadError ? (
        <Banner kind="error" sub={t`Prüfen Sie die Verbindung zu CMDBuild.`}>
          {loadError}
        </Banner>
      ) : null}

      {data && !schemaReady ? (
        <div className="card psirt-setup">
          <div className="card-body">
            <h2><Trans>Datenmodell ergänzen</Trans></h2>
            <p>
              <Trans>
                Für Verantwortlichkeiten fehlen in CMDBuild noch Teile des Datenmodells. Bestehende Daten
                bleiben beim Ergänzen unverändert.
              </Trans>
            </p>
            {canProvision ? (
              <button type="button" className="primary" onClick={() => void provision()} disabled={provisioning}>
                {provisioning ? <Spinner variant="on-accent" label={t`Ergänzt Datenmodell…`} /> : t`Datenmodell ergänzen`}
              </button>
            ) : (
              <p className="muted"><Trans>Das kann nur ein Administrator mit Schema-Berechtigung.</Trans></p>
            )}
          </div>
        </div>
      ) : null}

      {data && schemaReady ? (
        <>
          <KpiRow counters={kpiTiles} />

          <section className="card">
            <div className="card-head">
              <h2><Trans>PSIRT-Teams</Trans></h2>
              <span className="muted">
                <Trans>Meldestelle, CVD-Policy und Meldeweg gelten für alle zugeordneten Produkte.</Trans>
              </span>
            </div>
            <div className="card-body">
              {teams.length === 0 ? (
                <EmptyState
                  title={t`Noch kein PSIRT-Team angelegt`}
                  action={
                    canWrite ? (
                      <button type="button" className="primary" onClick={() => setDialog({ kind: 'team', team: null })}>
                        <Trans>Erstes PSIRT-Team anlegen</Trans>
                      </button>
                    ) : undefined
                  }
                >
                  <Trans>
                    Dafür brauchen Sie eine öffentliche Melde-Adresse, den Link zur CVD-Policy und den
                    Meldeweg an das koordinierende CSIRT.
                  </Trans>
                </EmptyState>
              ) : (
                <div className="psirt-teams">
                  {teams.map((team) => (
                    <TeamCard
                      key={team.id}
                      team={team}
                      canWrite={canWrite}
                      onEdit={() => setDialog({ kind: 'team', team })}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section id="psirt-results" className="card">
            <div className="card-head table-head">
              <h2>
                <Trans>Verantwortlichkeit je Produkt</Trans>
              </h2>
              {canWrite && teams.length > 0 && (data.rows.length ?? 0) > 0 ? (
                <button type="button" onClick={() => setDialog({ kind: 'assign' })}>
                  <Trans>Team zuweisen…</Trans>
                </button>
              ) : null}
            </div>
            <div className="table-toolbar">
              <input
                type="search"
                value={search}
                placeholder={t`Produkt, Team, Person…`}
                aria-label={t`Verantwortlichkeiten durchsuchen`}
                onChange={(event) => setSearch(event.target.value)}
              />
              <select
                value={filter}
                onChange={(event) => setFilter(event.target.value as Filter)}
                aria-label={t`Nach Status filtern`}
              >
                {FILTERS.map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {i18n._(entry.label)} ({filterCounts[entry.key]})
                  </option>
                ))}
              </select>
              <div className="table-toolbar-options">
                {filter !== 'alle' || search.trim() !== '' ? (
                  <button type="button" className="link" onClick={() => { setFilter('alle'); setSearch(''); }}>
                    <Trans>Filter zurücksetzen</Trans>
                  </button>
                ) : null}
                <span className="table-count" aria-live="polite">
                  {shown === total
                    ? t`${plural(total, { one: '# Produkt', other: '# Produkte' })}`
                    : t`${shown} von ${plural(total, { one: '# Produkt', other: '# Produkte' })}`}
                </span>
              </div>
            </div>
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.produkt_id}
              renderDetail={renderDetail}
              mobileBreakpoint={1080}
              pageSize={10}
              paginationResetKey={JSON.stringify([filter, search])}
              resetPageOnRowsChange={false}
              caption={t`Verantwortlichkeit je Produkt`}
              emptyText={
                data.rows.length === 0
                  ? t`Noch keine Produkte im Katalog. Unter „Stammdaten“ anlegen oder per CSV importieren.`
                  : t`Kein Produkt passt zu den Filtern.`
              }
            />
          </section>
        </>
      ) : null}

      {dialog?.kind === 'team' ? (
        <TeamFormModal
          team={dialog.team}
          canWrite={canWrite}
          onClose={() =>
            setDialog(dialog.returnTo ? { kind: 'product', row: dialog.returnTo } : null)
          }
          onSaved={() => void onTeamSaved(dialog.returnTo)}
        />
      ) : null}
      {dialog?.kind === 'product' && data ? (
        <ProductFormModal
          row={dialog.row}
          teams={teams}
          kategorien={data.kategorien}
          kategorieFolgen={data.kategorie_folgen}
          thresholds={data.thresholds}
          canWrite={canWrite}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
          onCreateTeam={() => setDialog({ kind: 'team', team: null, returnTo: dialog.row })}
        />
      ) : null}
      {dialog?.kind === 'assign' && data ? (
        <AssignTeamModal rows={data.rows} teams={teams} onClose={() => setDialog(null)} onSaved={onSaved} />
      ) : null}
    </>
  );
}

export default VerantwortlichkeitPage;
